import { useCallback, useEffect, useRef, useState } from "react";
import { useSyncStatusStore } from "@/store/syncStatusStore";
import {
  figureSvgUrl,
  parseFigureError,
  sourceHash,
  svgDataUrl,
  type FigureError,
} from "@/lib/tikzFigure";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/** After this long, a first compile adds the "first time is slow" note. */
export const SLOW_NOTE_MS = 4000;
/**
 * A source that changed without a save being seen (another tab's edit, merged
 * in) is fetched after this long anyway, once nothing is waiting to save.
 */
export const UNSEEN_SAVE_FALLBACK_MS = 8000;
/**
 * A figure just inserted (by Ask Lilia, a paste) is drawn before its first
 * save reaches the server, which answers 404. That means "not saved yet", not
 * "unreachable": the canvas keeps drawing and asks again after these delays.
 */
export const NOT_SAVED_RETRY_MS = [500, 1000, 2000, 4000, 8000];

export interface TikzDrawing {
  /** The drawing on show (a data URL), or null before the first one drew. */
  svg: string | null;
  /** A compile is in flight. With `svg`, a recompile; without, the first. */
  loading: boolean;
  /** The first compile has run past SLOW_NOTE_MS. */
  slow: boolean;
  error: FigureError | null;
  /** With an error: the last drawing that drew, when the server has one. */
  lastGood: string | null;
  /** Fetch again (Try again). */
  retry: () => void;
}

/** The last size each figure drew at, so a remount holds the same space. */
const lastSizes = new Map<string, { w: number; h: number }>();

export function rememberSize(blockId: string, w: number, h: number) {
  if (blockId && w > 0 && h > 0) lastSizes.set(blockId, { w, h });
}

export function lastSize(blockId: string): { w: number; h: number } | null {
  return lastSizes.get(blockId) ?? null;
}

/**
 * The drawing for a TikZ figure. The route renders the SAVED source, so a
 * source edited here is fetched once the sync has carried it to the server
 * (a save completed after the change, nothing pending); the source's hash
 * goes on the URL so the new drawing never comes from the old one's cache.
 * The source the figure opened with is already saved: it is fetched at once,
 * and so is everything when no sync runs for this document.
 */
export function useTikzDrawing(
  fetchWithAuth: Fetch,
  documentId: string | null | undefined,
  blockId: string | null | undefined,
  source: string,
  opts: {
    /** The split view is open: it draws, and the canvas waits. */
    paused?: boolean;
    /** A drawing the split view made, for the source with this hash. */
    known?: { hash: string; svg: string } | null;
  } = {},
): TikzDrawing {
  const hash = sourceHash(source);
  const knownSvg = opts.known && opts.known.hash === hash ? opts.known.svg : null;
  const paused = !!opts.paused;
  const [svg, setSvg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const [error, setError] = useState<FigureError | null>(null);
  const [lastGood, setLastGood] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The hash whose unseen-save fallback has come due.
  const [fallbackFor, setFallbackFor] = useState<string | null>(null);

  // When this hash became the one to draw; the opening source counts as saved.
  const openedHash = useRef(hash);
  const changedAt = useRef<number>(0);
  const prevHash = useRef(hash);
  // 404s in a row for this source (see NOT_SAVED_RETRY_MS).
  const notSavedTries = useRef(0);
  if (prevHash.current !== hash) {
    prevHash.current = hash;
    changedAt.current = Date.now();
    notSavedTries.current = 0;
  }

  const syncDoc = useSyncStatusStore((s) => s.documentId);
  const lastSyncedAt = useSyncStatusStore((s) => s.lastSyncedAt);
  const pendingCount = useSyncStatusStore((s) => s.pendingCount);
  const syncRuns = !!documentId && syncDoc === documentId;
  const savedSinceChange =
    hash === openedHash.current ||
    !syncRuns ||
    (pendingCount === 0 && ((lastSyncedAt?.getTime() ?? 0) >= changedAt.current || fallbackFor === hash));

  // A change no save ever covers is fetched after a while anyway.
  useEffect(() => {
    if (hash === openedHash.current) return;
    const t = setTimeout(() => setFallbackFor(hash), UNSEEN_SAVE_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [hash]);

  useEffect(() => {
    // The split view already drew this source: show its drawing, fetch nothing.
    if (knownSvg) {
      setSvg(knownSvg);
      setError(null);
      setLastGood(null);
      setLoading(false);
      return;
    }
    if (paused || !documentId || !blockId || !source.trim() || !savedSinceChange) return;
    let cancelled = false;
    let again: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);
    setSlow(false);
    (async () => {
      try {
        const res = await fetchWithAuth(figureSvgUrl(documentId, blockId, { v: hash }));
        if (cancelled) return;
        if (res.ok) {
          const text = await res.text();
          if (cancelled) return;
          setSvg(svgDataUrl(text));
          setError(null);
          setLastGood(null);
          notSavedTries.current = 0;
          return;
        }
        if (res.status === 404 && notSavedTries.current < NOT_SAVED_RETRY_MS.length) {
          // Not saved yet: still drawing, ask again shortly.
          again = setTimeout(() => setAttempt((n) => n + 1), NOT_SAVED_RETRY_MS[notSavedTries.current++]);
          return;
        }
        const e =
          res.status === 422
            ? parseFigureError(await res.json().catch(() => ({})))
            : { ...parseFigureError({}), kind: "unavailable" as const, message: "" };
        if (cancelled) return;
        setError(e);
        setLastGood(null);
        if (e.hasLastGood) {
          const lg = await fetchWithAuth(figureSvgUrl(documentId, blockId, { lastGood: true }));
          if (!cancelled && lg.ok) {
            const text = await lg.text();
            if (!cancelled) setLastGood(svgDataUrl(text));
          }
        }
      } catch {
        if (!cancelled) setError({ ...parseFigureError({}), kind: "unavailable", message: "" });
      } finally {
        if (!cancelled && !again) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(again);
    };
  }, [fetchWithAuth, documentId, blockId, hash, source, savedSinceChange, attempt, knownSvg, paused]);

  // The first compile's note: only while nothing has drawn yet.
  useEffect(() => {
    if (!loading || svg) return;
    const t = setTimeout(() => setSlow(true), SLOW_NOTE_MS);
    return () => clearTimeout(t);
  }, [loading, svg]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Waiting for the save counts as drawing: the old drawing dims meanwhile.
  const waiting = !!source.trim() && !savedSinceChange && !knownSvg;
  return { svg, loading: loading || waiting, slow, error, lastGood, retry };
}
